import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  MenuItem,
  Checkbox,
  Autocomplete,
  createFilterOptions,
  FormControlLabel,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Portal,
  Select,
  Snackbar,
  OutlinedInput,
  Popover,
} from "@mui/material";
import { KeyboardArrowDown, Search } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { AUDIENCE_EDIT_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import useTenantCountryIso from "../../hooks/useTenantCountry";
import axios from "axios";
import { getPhoneValidationError } from "../../common/phone-validation";
import { Add, Close } from "@mui/icons-material";
import { ColorsConstants } from "../../cssUtils/constants";

// A dial code alone can't identify the country (+1 is shared by US, CA, AS, ...); the full number can
const getCountryIsoFromPhone = (callingCode, phone) => {
  try {
    return parsePhoneNumber(`+${callingCode}${phone}`)?.country || "";
  } catch {
    return "";
  }
};

const getLatestNameSegment = (value) => {
  if (typeof value !== "string") return value ?? "";
  const parts = value
    .split(" & ")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return parts.length > 0 ? parts[parts.length - 1] : "";
};



const EditAudienceModal = ({
  audience,
  open,
  handleClose,
  tagOptions,
  setCreateTagModalOpen,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [tagAnchorEl, setTagAnchorEl] = useState(null);
  const tagSelectorRef = useRef(null);
  const tenantCountryIso = useTenantCountryIso();

  // The contact's saved country wins; the tenant's country only fills in when none was saved
  const getAudienceCountryIso = () =>
    audience.country_code
      ? audience.country_iso || getCountryIsoFromPhone(audience.country_code, audience.phone_no)
      : tenantCountryIso;
  const getAudienceCountryCode = () =>
    audience.country_code || getCountryCallingCode(tenantCountryIso);

  const [selectedCountryIso, setSelectedCountryIso] = useState(getAudienceCountryIso);

  const [form, setForm] = useState({
    name: getLatestNameSegment(audience.name),
    company: audience.company || "",
    countryCode: getAudienceCountryCode(),
    phone: audience.phone_no || "",
    email: audience.email || "",
    language: audience.language || "",
    gender: audience.gender || "",
    dob: audience.dob ? dayjs(audience.dob) : null,
    city: audience.city || "",
    state: audience.state || "",
    tags: audience.tags || [],
    marketingOptIn: !!audience.opted_in || false,
  });

  useEffect(() => {
    if (audience) {
      setSelectedCountryIso(getAudienceCountryIso());
      setForm({
        name: getLatestNameSegment(audience.name),
        company: audience.company || "",
        countryCode: getAudienceCountryCode(),
        phone: audience.phone_no || "",
        email: audience.email || "",
        language: audience.language || "",
        gender: audience.gender || "",
        dob: audience.dob ? dayjs(audience.dob) : null,
        city: audience.city || "",
        state: audience.state || "",
        tags: audience.tags || [],
        marketingOptIn: !!audience.opted_in || false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, tenantCountryIso]);

  const { getValidToken } = useAuth();

  const getCountryCodes = () => {
    const isoCodes = getCountries();

    return isoCodes
      .map((iso) => ({
        iso,
        name: en[iso] || iso,
        callingCode: getCountryCallingCode(iso),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const countryCodeOptions = getCountryCodes();

  const filterOptions = createFilterOptions({
    stringify: (option) => `${option.name} ${option.iso} +${option.callingCode}`,
  });

  const selectedCountryOption =
    countryCodeOptions.find((option) => option.iso === selectedCountryIso) ||
    countryCodeOptions.find((option) => option.callingCode === form.countryCode) ||
    null;

  useEffect(() => {
    if (!selectedCountryIso) {
      const matched = countryCodeOptions.find((option) => option.callingCode === form.countryCode);
      if (matched) {
        setSelectedCountryIso(matched.iso);
      }
    }
  }, [countryCodeOptions, form.countryCode, selectedCountryIso]);

  const handleCountrySelect = (option) => {
    if (!option) return;
    setSelectedCountryIso(option.iso);
    handleChange("countryCode", option.callingCode);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === "phone" ? value.replace(/\D/g, "") : value,
    }));
  };

  const handleCancel = () => {
    setForm({
      name: "",
      countryCode: getCountryCallingCode(tenantCountryIso),
      phone: "",
      email: "",
      dob: dayjs(),
      tags: [],
      marketingOptIn: false,
    });
    setError(null);
    setSuccess(false);
    setTagSearchQuery("");
    setTagAnchorEl(null);
    handleClose();
  };

  const handleAlertClose = () => {
    setError(null);
    setSuccess(false);
  };

  const phoneError = form.phone
    ? getPhoneValidationError(form.phone, selectedCountryOption?.iso, form.countryCode)
    : null;

  const validateForm = () => {
    if (!form.countryCode.trim()) return "Country code is required.";
    const phoneValidationError = getPhoneValidationError(form.phone, selectedCountryOption?.iso, form.countryCode);
    if (phoneValidationError) return phoneValidationError;
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Invalid email format.";
    if (form.dob && !dayjs(form.dob).isValid()) return "Invalid Date of birth";
    return null;
  };

  const editAudience = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setSuccess(false);
      setLoading(false);
      return;
    }

    try {
      const payload = {
        name: form.name,
        company: form.company || "",
        phone_no: form.phone,
        country_code: form.countryCode,
        country_iso: selectedCountryOption?.iso || "",
        email: form.email || "",
        language: form.language || "",
        gender: form.gender || "",
        date_of_birth: form.dob
          ? dayjs(form.dob).format("DD-MM-YYYY")
          : form.dob,
        city: form.city || "",
        state: form.state || "",
        tags: form.tags.map((tag) => tag.id),
        opt_in: form.marketingOptIn,
      };

      const apiUrl = AUDIENCE_EDIT_ENDPOINT;

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await axios.patch(`${apiUrl}${audience.id}/`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (response.status !== 200 && response.status !== 201) {
        throw new Error({ message: "Error: " + response.data.error });
      }

      setSuccess(true);
      setTimeout(() => {
        handleCancel();
      }, 1500);
    } catch (err) {
      setSuccess(false);
      if (err.response && err.response.data) {
        let data = err.response?.data;

        console.log("Data: ", data, typeof data);

        if (typeof data === "object") {
          const messages = [];
          for (const key in data) {
            if (Array.isArray(data[key])) {
              messages.push(`${data[key].join("\n ")}`);
            } else {
              messages.push(`${data[key]}`);
            }
          }
          setError(messages.join(" | "));
        } else if (typeof data === "string") {
          setError(data);
        } else {
          setError("Failed to edit audience.");
        }
      } else if (err.message) {
        setError("Failed to edit audience: " + err.message);
      } else if (err.error) {
        setError("Failed to edit audience: " + err.error);
      } else {
        setError("Failed to edit audience.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {(Boolean(error) || success) && (
        <Portal>
          <Snackbar
            open={Boolean(error) || success}
            autoHideDuration={4000}
            onClose={handleAlertClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            sx={{
              position: "fixed",
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
          >
            <Alert
              onClose={handleAlertClose}
              severity={success ? "success" : "error"}
              variant="filled"
              sx={{
                width: "100%",
                borderRadius: "10px",
                fontWeight: 500,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              {success ? "Audience updated successfully" : error}
            </Alert>
          </Snackbar>
        </Portal>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        sx={{ zIndex: 1202 }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Edit Audience
          <IconButton aria-label="close" onClick={handleCancel}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} p={2}>
            <TextField
              label="Company"
              value={form.company}
              onChange={(e) => handleChange("company", e.target.value)}
              fullWidth
            />

            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              fullWidth
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ minWidth: 280 }}>
                <Autocomplete
                  options={countryCodeOptions}
                  value={selectedCountryOption}
                  onChange={(_, newValue) => handleCountrySelect(newValue)}
                  filterOptions={filterOptions}
                  getOptionLabel={(option) =>
                    option ? `${option.name} (${option.iso}) +${option.callingCode}` : ""
                  }
                  isOptionEqualToValue={(option, value) => option.iso === value.iso}
                  disableClearable
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.iso}>
                      {option.name} ({option.iso}) +{option.callingCode}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Country Code *" fullWidth />
                  )}
                />
              </Box>

              <TextField
                label="Phone"
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                error={Boolean(phoneError)}
                helperText={phoneError || ""}
                inputProps={{ inputMode: "numeric" }}
                sx={{ flex: 1 }}
                required
              />
            </Box>


            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              fullWidth
            />

            <TextField
              label="Language"
              value={form.language}
              onChange={(e) => handleChange("language", e.target.value)}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select
                value={form.gender || ""}
                label="Gender"
                onChange={(e) => handleChange("gender", e.target.value)}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="N/A">N/A</MenuItem>
              </Select>
            </FormControl>

            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="en-gb"
            >
              <Box>
                <DemoItem label="">
                  <DatePicker
                    label="Date of Birth"
                    value={form.dob}
                    onChange={(newValue) => handleChange("dob", newValue)}
                    disableFuture
                    format="DD-MM-YYYY"
                    slotProps={{
                      textField: { fullWidth: true },
                    }}
                  />
                </DemoItem>
              </Box>
            </LocalizationProvider>

            <TextField
              label="City"
              value={form.city}
              onChange={(e) => handleChange("city", e.target.value)}
              fullWidth
            />

            <TextField
              label="State"
              value={form.state}
              onChange={(e) => handleChange("state", e.target.value)}
              fullWidth
            />

            {/* Tags */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              {/* Tag selector — styled like other FormControl fields */}
              <FormControl sx={{ flex: 1 }}>
                <InputLabel
                  backgroundColor="#FFF"
                  zIndex={10}
                  shrink={form.tags.length > 0 || Boolean(tagAnchorEl)}
                  sx={{
                    pl: 0.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    pointerEvents: "none",
                    backgroundColor: "white",
                  }}
                >
                  Tags
                  {form.tags.length > 0 && (
                    <Box
                      component="span"
                      sx={{
                        mx: 0.5,
                        backgroundColor: ColorsConstants.primaryBlue100,
                        color: "#fff",
                        borderRadius: "10px",
                        px: 0.75,
                        fontSize: "12px",
                        lineHeight: "18px",
                        display: "inline-block",
                        fontWeight: 600,
                      }}
                    >
                      {form.tags.length}
                    </Box>
                  )}
                </InputLabel>
                <Box
                  ref={tagSelectorRef}
                  onClick={(e) => setTagAnchorEl(e.currentTarget)}
                  sx={{
                    border: "1px solid rgba(0,0,0,0.23)",
                    borderRadius: "4px",
                    px: 1.75,
                    py: 0.875,
                    minHeight: "56px",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    position: "relative",
                    backgroundColor: "transparent",
                    transition: "border-color 0.2s",
                    "&:hover": { borderColor: "rgba(0,0,0,0.87)" },
                    ...(tagAnchorEl && {
                      borderColor: ColorsConstants.primaryBlue100,
                      borderWidth: "2px",
                      px: "13px",
                      py: "6.5px",
                    }),
                  }}
                >
                  {form.tags.map((tag, index) => (
                    <Chip
                      key={tag.id ?? index}
                      label={tag.name}
                      onDelete={(e) => {
                        e.stopPropagation();
                        setForm((prev) => ({
                          ...prev,
                          tags: prev.tags.filter((_, i) => i !== index),
                        }));
                      }}
                      size="small"
                      sx={{
                        backgroundColor: ColorsConstants.primaryBlue100,
                        color: "#fff",
                        "& .MuiChip-deleteIcon": {
                          color: "rgba(255,255,255,0.7)",
                          "&:hover": { color: "#fff" },
                        },
                      }}
                    />
                  ))}
                  {form.tags.length === 0 && !tagAnchorEl && (
                    <Typography
                      variant="body2"
                      sx={{ color: "transparent", userSelect: "none" }}
                    >
                      &nbsp;
                    </Typography>
                  )}
                  <KeyboardArrowDown
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: tagAnchorEl
                        ? "translateY(-50%) rotate(180deg)"
                        : "translateY(-50%)",
                      transition: "transform 0.2s",
                      color: "rgba(0,0,0,0.54)",
                      fontSize: "20px",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </FormControl>

              {/* Create Tag Button — same height as field */}
              <Button
                variant="outlined"
                startIcon={<Add />}
                sx={{
                  textTransform: "none",
                  borderColor: ColorsConstants.primaryBlue100,
                  color: ColorsConstants.primaryBlue100,
                  height: "56px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                onClick={() => setCreateTagModalOpen(true)}
              >
                Create Tag
              </Button>
            </Box>

            {/* Tag Popover — floats above the DOM, closes on outside click */}
            <Popover
              open={Boolean(tagAnchorEl)}
              anchorEl={tagAnchorEl}
              onClose={() => {
                setTagAnchorEl(null);
                setTagSearchQuery("");
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              disableAutoFocus
              slotProps={{
                paper: {
                  sx: {
                    width: tagSelectorRef.current?.offsetWidth ?? 300,
                    mt: 0.5,
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: "4px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    overflow: "hidden",
                  },
                },
              }}
            >
              {/* Search */}
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Search sx={{ color: "text.secondary", fontSize: "18px" }} />
                <OutlinedInput
                  autoFocus
                  placeholder="Search tags..."
                  value={tagSearchQuery}
                  onChange={(e) => setTagSearchQuery(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{
                    "& fieldset": { border: "none" },
                    "& .MuiOutlinedInput-input": { p: 0, fontSize: "14px" },
                  }}
                />
              </Box>

              {/* Tag list */}
              <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
                {tagOptions
                  .filter((tag) =>
                    tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase()),
                  )
                  .map((tag) => {
                    const isSelected = form.tags.some(
                      (t) => (t.id ?? t) === tag.id,
                    );
                    return (
                      <Box
                        key={tag.id}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            tags: isSelected
                              ? prev.tags.filter((t) => (t.id ?? t) !== tag.id)
                              : [...prev.tags, tag],
                          }))
                        }
                        sx={{
                          px: 1.5,
                          py: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          readOnly
                          tabIndex={-1}
                          disableRipple
                          size="small"
                          sx={{
                            p: 0,
                            color: ColorsConstants.primaryBlue100,
                            "&.Mui-checked": {
                              color: ColorsConstants.primaryBlue100,
                            },
                          }}
                        />
                        <Typography variant="body2">{tag.name}</Typography>
                      </Box>
                    );
                  })}
                {tagOptions.filter((tag) =>
                  tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase()),
                ).length === 0 && (
                  <Typography
                    variant="body2"
                    sx={{ px: 2, py: 1.5, color: "text.secondary" }}
                  >
                    No tags found
                  </Typography>
                )}
              </Box>
            </Popover>

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.marketingOptIn}
                  onChange={(e) =>
                    handleChange("marketingOptIn", e.target.checked)
                  }
                />
              }
              label="Marketing Opt-In"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={editAudience}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditAudienceModal;